// ─── 🎁 Il premio a chi organizza, in crediti ────────────────────────────────
//
// Voce 33, mockup approvato il 26/09/2026 (strade 1A · 2A · 3A).
//
// 🗣️ *«Ogni volta che una persona nell'arco di sette giorni … organizza una
// partita gli vengono accreditati»* · *«due campi lo stesso giorno si contano»*
// · *«solo dopo ogni partita … parzialmente accreditate, ma non ancora
// disponibili per pagare»* · *«devo fare una cosa di crediti»*.
//
// ⚖️ **Crediti, non denaro**: 1 credito = 1,00 € di sconto sulla quota di una
// partita. Non si prelevano, non si rimborsano, non si passano ad altri
// (DPR 430/2001, art. 4 e art. 6 lett. c).
import { z } from 'zod';
import { dataSchema } from './circolo.js';
import { giorniFra } from './griglia.js';
import { lottiDelBorsellino } from './caparra.js';
/** Quanto sconto vale un credito, in centesimi. Fisso: «1 credito = 1,00 €». */
export const VALORE_CREDITO_CENTS = 100;
/** La finestra: un'altra partita sua entro 7 giorni di calendario, 7 compreso, anche lo stesso giorno. */
export const GIORNI_DEL_PREMIO = 7;
// ── Le impostazioni ─────────────────────────────────────────────────────────
export const impostazioniPremioSchema = z.object({
    /** Spento di partenza: si accende dopo il commercialista. */
    attivo: z.boolean(),
    /** Da che giorno contano le partite: niente premi per le partite di prima. */
    dal: dataSchema.nullable(),
    creditiPerPartita: z.number().int().min(1).max(20),
    // 🔄 Voce 36 (27/09/2026): quanto valgono i Crediti e quando si avvisa non
    // stanno più qui ⇒ `crediti.ts`, una regola sola con quelli dell'acconto.
});
export const IMPOSTAZIONI_PREMIO_DI_PARTENZA = {
    attivo: false,
    dal: null,
    creditiPerPartita: 2,
};
// ── La regola dei 7 giorni ──────────────────────────────────────────────────
/**
 * ⭐ Una partita ha una compagna se chi l'ha organizzata ne ha un'ALTRA, ancora
 * in piedi, entro 7 giorni di calendario (7 compreso), prima o dopo — anche la
 * stessa sera: due campi per un torneo si fanno premio a vicenda.
 */
export function haCompagna(partita, altre) {
    return altre.some((q) => q.id !== partita.id && Math.abs(giorniFra(partita.date, q.date)) <= GIORNI_DEL_PREMIO);
}
// ── I movimenti, e i crediti vivi ───────────────────────────────────────────
export const TIPI_MOVIMENTO_PREMIO = [
    'premio',
    'ripristino',
    'storno_uso',
    'no_show',
    'uso',
    'scadenza',
];
export const ENTRANO = new Set(['premio', 'ripristino', 'storno_uso']);
export const ETICHETTE_MOVIMENTO_PREMIO = {
    premio: 'Premio organizzatore',
    ripristino: 'Premio rimesso (no-show annullato)',
    storno_uso: 'Tornati da uno storno',
    no_show: 'Tolti per no-show',
    uso: 'Usati per pagare',
    scadenza: 'Scaduti',
};
/**
 * ⭐ I crediti si contano come il credito della caparra: a lotti con scadenza,
 * consumati prima quelli che scadono prima (⇒ `lottiDelBorsellino`). Il saldo
 * non si tiene scritto da nessuna parte: si ricalcola dalla storia.
 */
export function lottiDeiCrediti(movimenti) {
    return lottiDelBorsellino(movimenti.map((m) => ({
        id: m.id,
        direction: ENTRANO.has(m.kind) ? (m.kind === 'storno_uso' ? 'refund' : 'recharge') : 'charge',
        amountCents: m.credits,
        recordedAt: m.recordedAt,
        expiresAt: m.expiresAt,
        lotTransactionId: m.kind === 'scadenza' ? m.lotId : null,
        reversesTransactionId: m.kind === 'storno_uso' ? m.reversesId : null,
    })));
}
/** I crediti spendibili adesso: i lotti vivi, senza quelli scaduti che aspettano la loro riga. */
export function creditiDisponibili(l, adesso) {
    let n = Math.max(0, l.liberoCents);
    for (const x of l.lotti) {
        if (x.remainingCents > 0 && Date.parse(x.expiresAt) > adesso.getTime())
            n += x.remainingCents;
    }
    return n;
}
// ── Cosa vedono la Segreteria e il bot ──────────────────────────────────────
export const partitaInArrivoSchema = z.object({
    bookingId: z.uuid(),
    date: dataSchema,
    time: z.string(),
    courtName: z.string(),
    credits: z.number().int(),
});
export const crediteInScadenzaSchema = z.object({
    credits: z.number().int(),
    expiresOn: dataSchema,
});
export const movimentoPremioSchema = z.object({
    id: z.uuid(),
    kind: z.enum(TIPI_MOVIMENTO_PREMIO),
    /** Col segno: + entrano, − escono. */
    credits: z.number().int(),
    recordedAt: z.string(),
    /** La partita, detta per esteso («mer 7/10 · 20:00 · Campo 2»), quando c'è. */
    booking: z.string().nullable(),
});
export const premioDelSocioSchema = z.object({
    active: z.boolean(),
    creditsPerMatch: z.number().int(),
    creditValueCents: z.number().int(),
    months: z.number().int(),
    available: z.number().int(),
    incoming: z.array(partitaInArrivoSchema),
    expiring: z.array(crediteInScadenzaSchema),
    movements: z.array(movimentoPremioSchema),
});
/** I crediti di chi è in campo, per il mezzo «🎁 Crediti premio» della scheda. */
export const creditiDelGiocatoreSchema = z.object({
    memberId: z.uuid(),
    credits: z.number().int(),
});
//# sourceMappingURL=premio.js.map